import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Alert,
  Button,
  Card,
  CardList,
  Checkbox,
  EmptyState,
  ErrorState,
  IconCheckCircle,
  Modal,
  Page,
  PageHeader,
  Segmented,
  Skeleton,
} from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import type { BillingCycle, PaymentMethod, TierCode } from '../api/billingApi';
import { buildDefaultCardAlias, paymentMethodDisplayName } from '../components/PaymentMethodCardBody';
import { PaymentMethodTile } from '../components/PaymentMethodTile';
import { TIER_COPY, getTrialMonths } from '../constants/tierCopy';
import { generateCustomerKey, requestBillingAuth, requestPayment } from '../lib/toss';

const SLOW_RESPONSE_MS = 10000;
const VALID_TIER_CODES: TierCode[] = ['FREE', 'STARTER', 'PRO', 'ALLIN'];

/** 저장된 카드로 자동결제(구독, 기존 흐름) vs 카드를 등록하지 않고 매번 토스 결제창에서 직접
 *  결제하는 1회성 결제(2026-09-10, BE POST /subscription/one-time-payment 연동). 1회성 결제는
 *  빌링키가 안 생기므로 다음 결제일에 자동 청구되지 않고 곧바로 FREE로 전환된다(정책 확정). */
type PaymentMode = 'AUTO' | 'ONE_TIME';

/** requestPayment() successUrl 리다이렉트로 돌아왔을 때 실패 사유(message 쿼리파라미터)가
 *  카드 등록 실패(registerCardMutation)와 1회성 결제 실패 중 어느 쪽인지 구분하는 용도 — 둘 다 같은
 *  화면으로 돌아오고 실패 시 파라미터명이 똑같이 message라 겹친다(PaymentMethodPage.tsx의
 *  PENDING_METHOD_KEY와 동일한 이유의 세션스토리지 마커). */
const PENDING_ONE_TIME_PAYMENT_KEY = 'billing_pending_one_time_payment';

function formatWon(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatPaymentMethodLabel(method: PaymentMethod): string {
  const name = paymentMethodDisplayName(method);
  if (method.methodType === 'CARD' && method.cardLast4) {
    return `${name} •••• ${method.cardLast4}`;
  }
  return name;
}

function extractErrorReason(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { data?: string; message?: string } } })?.response?.data;
  return data?.data ?? data?.message ?? fallback;
}

/**
 * C-11(결제 확인·진행 화면). 진입경로: C-6(TierChangePage)의 "지금 업그레이드" 버튼 — 다운그레이드는
 * 이 화면을 거치지 않는다(C-5도 마찬가지, 다음 결제일 자동 반영).
 * <p>
 * ?mode=trial이면 무료체험 확인 플로우 — PricingPlansPage의 "1개월 무료체험 시작" 버튼이 로그인
 * 상태에서 이 화면으로 보낸다(사용자 요청, 2026-09-03. 이전엔 이 화면을 안 거치고 확인모달에서
 * 바로 시작했음). 무료체험은 여전히 카드 등록이 필수가 아니다(2026-08-27 확정 사항 유지) — 그래서
 * 업그레이드 미리보기(getTierChangePreview, 기존 유료전환 전제 API)를 안 쓰고 공개
 * 카탈로그(getPricingPlans)에서 정가만 안내용으로 가져온다.
 */
export function CheckoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tierParam = searchParams.get('tier');
  const targetTier: TierCode | null = tierParam && VALID_TIER_CODES.includes(tierParam as TierCode)
    ? (tierParam as TierCode)
    : null;
  const isTrial = searchParams.get('mode') === 'trial';
  // 무료체험(startTrial)은 이 값이 없으면 'monthly'로 기본값을 둬야 하지만, 업그레이드(첫 유료 전환
  // 포함) 미리보기는 파라미터가 없을 때 undefined로 남겨야 BE가 기존 구독 주기로 폴백한다(계약 유지) —
  // 그래서 원본(cycleParam, null 가능)과 체험 전용 기본값(trialBillingCycle)을 분리해 둔다.
  const cycleParam = searchParams.get('cycle') as BillingCycle | null;
  const trialBillingCycle = cycleParam ?? 'monthly';
  // C-8(추천인 초대) — PricingPlansPage에서 ?ref=로 그대로 넘어온 코드. 2026-09-23부로 코드
  // 소유권이 sv-member로 이관되며 sv-billing은 코드 자체를 모르게 됐다 — startTrial엔 이 문자열이
  // 아니라 아래 referralCodeQuery로 해석한 referrerMemberId를 실어보낸다.
  const referralCode = searchParams.get('ref') ?? undefined;
  const trialMonths = getTrialMonths(Boolean(referralCode));
  // 코드 → referrerMemberId 해석(로그인 상태 진입 경로 전용). 무효한 코드여도 조용히 무시하고
  // 일반 체험으로 진행한다(회원가입 화면과 동일 원칙) — 에러 UI 없음, referrerMemberId만 undefined.
  const referralCodeQuery = useQuery({
    queryKey: ['billing', 'referralCodeResolve', referralCode],
    queryFn: async ({ signal }) => (await billingApi.validateReferralCode(referralCode as string, signal)).data.data,
    enabled: Boolean(referralCode),
    staleTime: Infinity,
    retry: 2,
  });
  const referrerMemberId = referralCodeQuery.data?.valid
    ? referralCodeQuery.data.referrerMemberId ?? undefined
    : undefined;

  const [agreed, setAgreed] = useState(false);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('AUTO');
  // 결제수단 타일을 관리 화면과 동일한 완전한 형태(PaymentMethodTile)로 쓰면서 함께 필요해진 상태
  // (2026-09-16, 사용자 요청 — 별칭 수정·기본 설정·삭제까지 관리 화면과 동일하게 노출).
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [slowResponse, setSlowResponse] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // apiClient(axios)는 10초 자체 타임아웃이 있어(packages/shared/src/api/client.ts) 서버가 아직
  // 처리 중이어도 mutation은 10초에 settle(에러)돼버린다 — mutation.isPending을 그대로 "확정 여부"로
  // 쓰면 클라이언트 타임아웃을 "서버가 실패라고 답함"과 구분 못 해 이중결제 위험이 생긴다(2026-09-07
  // 발견·수정). 그래서 "아직 결과를 확신할 수 없음"은 이 상태로 별도 관리하고, 서버가 실제로 응답한
  // 확정 실패(아래 onError의 error.response 존재 여부)에만 false로 내린다 — 성공은 onSuccess 또는
  // 폴링(statusPollQuery)이 내린다.
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // awaitingConfirmation은 handleOneTimePayment가 토스 결제창으로 실제 리다이렉트(navigate away)하기
  // 직전에도 true가 된다(아래) — 이 구간은 beforeunload 가드가 막아야 할 "서버가 처리 중인데 몰래
  // 이탈"이 아니라 우리가 의도적으로 떠나보내는 정상 흐름이라, 가드에서 제외해야 한다(2026-09-11
  // 리뷰에서 발견 — 안 그러면 결제 버튼을 누를 때마다 "이 사이트를 떠나시겠습니까?"가 뜬다).
  const redirectingToTossRef = useRef(false);

  const previewQuery = useQuery({
    queryKey: ['billing', 'tierChangePreview', targetTier, cycleParam],
    // cycleParam이 있으면(요금제 비교 화면에서 주기 토글을 바꿔둔 채 업그레이드 클릭 — 특히 FREE·
    // 해지 후 재구독 등 기존 결제 주기가 없는/의미 없는 첫 유료 전환) 그대로 넘겨 그 주기로 첫 결제가
    // 청구되게 한다. 없으면 undefined로 둬서 BE가 기존 구독 주기로 폴백한다(2026-09-15 발견·수정 —
    // 예전엔 이 값을 아예 안 넘겨서 요금제 비교 화면에서 연간을 골라도 첫 결제가 항상 월간으로 나갔음).
    queryFn: async ({ signal }) =>
      (await billingApi.getTierChangePreview(targetTier as TierCode, cycleParam ?? undefined, signal)).data.data,
    enabled: targetTier != null && !isTrial,
  });

  // 무료체험 정가 안내용 — PricingPlansPage와 같은 쿼리키라 캐시가 있으면 재조회 없이 즉시 뜬다.
  const plansQuery = useQuery({
    queryKey: ['billing', 'pricingPlans'],
    queryFn: async ({ signal }) => (await billingApi.getPricingPlans(signal)).data.data,
    enabled: isTrial,
  });

  const paymentMethodsQuery = useQuery({
    queryKey: ['billing', 'paymentMethods'],
    queryFn: async ({ signal }) => (await billingApi.getPaymentMethods(signal)).data.data,
  });
  // 이번 결제에 쓸 카드 — 기본카드와는 별개 개념이다(2026-09-18 사용자 결정: "자동결제는 기본카드로,
  // 결제창에서 결제하는 순간은 사용자가 선택한 카드로"). 처음엔 편의상 기본카드(정렬상 첫 항목)로
  // 초기화하고, 이후엔 사용자가 명시적으로 다른 카드를 고르기 전까진 그대로 유지한다(sticky) —
  // data[0]에서 매번 다시 파생시키면(수정 전) 사용자가 "기본으로 설정"만 눌러도(카드 목록이 재조회돼
  // 정렬이 바뀌면서) 결제 대상 카드가 사용자 모르게 같이 바뀌는 버그가 있었다(검수에서 발견). 선택된
  // 카드가 삭제돼 목록에서 사라진 경우만 다시 기본카드로 되돌린다.
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | null>(null);
  useEffect(() => {
    if (!paymentMethodsQuery.data) return;
    const stillExists = paymentMethodsQuery.data.some((m) => m.paymentMethodId === selectedPaymentMethodId);
    if (!stillExists) {
      setSelectedPaymentMethodId(paymentMethodsQuery.data[0]?.paymentMethodId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodsQuery.data]);
  const selectedMethodId = selectedPaymentMethodId;
  const selectedMethod = paymentMethodsQuery.data?.find((m) => m.paymentMethodId === selectedMethodId);

  const [cardRegistrationError, setCardRegistrationError] = useState<string | null>(null);
  const [registeringCard, setRegisteringCard] = useState(false);
  const processedAuthKeyRef = useRef<string | null>(null);

  // 결제수단이 없거나 카드를 추가로 등록하고 싶을 때 이 화면에서 바로 토스 카드 등록 결제창을 띄운다
  // (사용자 요청 — 이전엔 결제수단 등록·변경 화면으로 이동시켰음). successUrl/failUrl을 현재
  // 화면(tier 등 쿼리스트링 포함)으로 그대로 돌려받아 결제 진행 상태를 잃지 않는다
  // (PaymentMethodPage.tsx의 등록 콜백 처리와 동일 패턴).
  const registerCardMutation = useMutation({
    mutationFn: (payload: { authKey: string; customerKey: string }) => billingApi.registerCard(payload),
    onSuccess: async (response) => {
      setCardRegistrationError(null);
      toast.success('카드가 등록되었어요');
      // 결제 화면에서 등록한 카드는 "이번 결제에 쓸 다른 수단"이라는 뜻이라 곧바로 선택 상태로
      // 바꿔서 이번 결제부터 적용되게 한다 — 기본 결제수단(IS_DEFAULT)은 건드리지 않는다
      // (2026-09-18 정책 확정 — "선택"과 "기본카드 변경"은 별개 동작).
      const newMethod = response.data.data;
      if (newMethod) {
        setSelectedPaymentMethodId(newMethod.paymentMethodId);
        // 등록 직후엔 별칭이 비어 있어(BE cardAlias=null) "카드사명 + 뒷자리 4자리"를 기본값으로
        // 바로 저장한다(2026-09-21, 사용자 요청) — 실패해도 등록 자체는 이미 끝났으니 조용히 무시.
        try {
          await billingApi.updateCardAlias(newMethod.paymentMethodId, buildDefaultCardAlias(newMethod));
        } catch {
          // no-op
        }
      }
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: (error: unknown) => {
      const reason = extractErrorReason(error, '카드 등록에 실패했습니다');
      toast.error(reason);
      setCardRegistrationError(reason);
    },
  });

  const processedPaymentKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // 1회성 결제(requestPayment) 성공 리다이렉트 — paymentKey/orderId는 카드 등록 리다이렉트
    // (authKey/customerKey)와 파라미터명이 겹치지 않아 바로 구분 가능하다. amount는 토스가 함께
    // 실어보내지만 서버가 안 쓰므로(위변조 방지, upgradeTier와 동일 원칙) 값 자체를 읽지 않는다 —
    // 요구조건에서도 빼서, 토스가 언젠가 amount를 안 실어보내도 승인 요청이 막히지 않게 한다.
    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    if (paymentKey && orderId) {
      // React StrictMode 이중 실행 방지 — paymentKey도 1회성 값이라 같은 값으로 두 번 승인 요청하면
      // 두 번째는 토스에서 실패한다(authKey와 동일 이유).
      if (processedPaymentKeyRef.current === paymentKey) return;
      processedPaymentKeyRef.current = paymentKey;
      sessionStorage.removeItem(PENDING_ONE_TIME_PAYMENT_KEY);
      oneTimePaymentMutation.mutate({ paymentKey, orderId });
      const next = new URLSearchParams(searchParams);
      next.delete('paymentKey');
      next.delete('orderId');
      next.delete('amount');
      setSearchParams(next, { replace: true });
      return;
    }

    const authKey = searchParams.get('authKey');
    const customerKey = searchParams.get('customerKey');
    if (authKey && customerKey) {
      // React StrictMode 이중 실행 방지 — authKey는 1회성 토큰이라 같은 값으로 두 번 호출하면
      // 두 번째는 토스에서 "존재하지 않는 정보"로 실패한다.
      if (processedAuthKeyRef.current === authKey) return;
      processedAuthKeyRef.current = authKey;
      registerCardMutation.mutate({ authKey, customerKey });
      const next = new URLSearchParams(searchParams);
      next.delete('authKey');
      next.delete('customerKey');
      setSearchParams(next, { replace: true });
      return;
    }

    const failMessage = searchParams.get('message');
    if (failMessage) {
      // 1회성 결제 시도 중 실패했던 것인지(PENDING_ONE_TIME_PAYMENT_KEY 마커) 카드 등록 실패인지
      // 구분해서 서로 다른 배너(paymentError vs cardRegistrationError)에 꽂는다.
      const wasOneTimePayment = sessionStorage.getItem(PENDING_ONE_TIME_PAYMENT_KEY) != null;
      sessionStorage.removeItem(PENDING_ONE_TIME_PAYMENT_KEY);
      toast.error(failMessage);
      if (wasOneTimePayment) {
        setPaymentError(failMessage);
        setAwaitingConfirmation(false);
      } else {
        setCardRegistrationError(failMessage);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('message');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRegisterCard = async () => {
    setCardRegistrationError(null);
    setRegisteringCard(true);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      await requestBillingAuth({
        method: 'CARD',
        customerKey: generateCustomerKey(),
        successUrl: returnUrl,
        failUrl: returnUrl,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '등록을 시작하지 못했어요');
      setRegisteringCard(false);
    }
  };

  // "1회만 결제" 모드의 결제 버튼 — 카드를 등록하지 않고 토스 결제창(requestPayment)을 그 자리에서
  // 열어 승인까지 끝낸다. 성공하면 successUrl로 paymentKey/orderId/amount를 받아 돌아오고, 그
  // 리다이렉트를 처리하는 useEffect가 oneTimePaymentMutation으로 서버 최종 승인을 요청한다.
  const handleOneTimePayment = async () => {
    if (!preview || !targetTier) return;
    setPaymentError(null);

    // 프로레이션 차액이 0원(주기 끝자락 업그레이드 등)이면 결제할 금액 자체가 없다 — AUTO 모드의
    // upgradeTier가 amount<=0일 때 토스 호출 없이 바로 처리하는 것과 동일하게(BE confirmOneTimePayment도
    // amount<=0이면 토스 confirm을 부르지 않는다), 실제 카드 결제 없이 승인만 요청한다. paymentKey는
    // amount<=0 분기에서 서버가 아예 안 읽으므로 플레이스홀더로 충분하다.
    if (preview.proratedDiff <= 0) {
      oneTimePaymentMutation.mutate({ paymentKey: 'ZERO_AMOUNT', orderId: crypto.randomUUID() });
      return;
    }

    setAwaitingConfirmation(true);
    redirectingToTossRef.current = true;
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      sessionStorage.setItem(PENDING_ONE_TIME_PAYMENT_KEY, '1');
      await requestPayment({
        customerKey: generateCustomerKey(),
        amount: preview.proratedDiff,
        orderId: crypto.randomUUID(),
        orderName: `${TIER_COPY[targetTier].displayName} 요금제 1회 결제`,
        successUrl: returnUrl,
        failUrl: returnUrl,
      });
    } catch (error) {
      redirectingToTossRef.current = false;
      sessionStorage.removeItem(PENDING_ONE_TIME_PAYMENT_KEY);
      toast.error(error instanceof Error ? error.message : '결제를 시작하지 못했어요');
      setAwaitingConfirmation(false);
    }
  };

  // 결제에 쓸 카드 선택 — BE는 항상 "기본 결제수단"으로만 청구하므로(attemptCharge 참고), 여기서
  // 다른 카드를 기본으로 바꾸면 이번 결제부터 그 카드로 청구되게 한다. PaymentMethodTile의
  // "기본으로 설정" 버튼이 이 뮤테이션을 그대로 쓴다(관리 화면과 동일한 액션).
  const setDefaultMutation = useMutation({
    mutationFn: (paymentMethodId: number) => billingApi.setDefaultPaymentMethod(paymentMethodId),
    onSuccess: () => {
      toast.success('결제에 사용할 카드를 변경했어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: () => toast.error('카드 변경에 실패했습니다. 잠시 후 다시 시도해주세요.'),
  });

  // 아래 둘은 PaymentMethodPage.tsx와 동일한 관례(삭제는 확인 모달 경유, 별칭은 인라인 저장) —
  // PaymentMethodTile을 관리 화면과 동일하게 쓰면서 함께 필요해졌다(2026-09-16 사용자 요청).
  const deleteMutation = useMutation({
    mutationFn: (paymentMethodId: number) => billingApi.deletePaymentMethod(paymentMethodId),
    onSuccess: () => {
      toast.success('결제수단을 삭제했어요');
      setPendingDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: (error: unknown) => {
      toast.error(extractErrorReason(error, '삭제에 실패했습니다'));
      setPendingDeleteId(null);
    },
  });

  const updateAliasMutation = useMutation({
    mutationFn: (payload: { paymentMethodId: number; cardAlias: string }) =>
      billingApi.updateCardAlias(payload.paymentMethodId, payload.cardAlias),
    onSuccess: () => {
      toast.success('별칭을 저장했어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    },
    onError: (error: unknown) => toast.error(extractErrorReason(error, '별칭 저장에 실패했습니다')),
  });

  /** 서버가 실제로 응답했는지(비즈니스 성공/실패) 판정 — axios 자체 타임아웃(10초, 클라이언트가
   *  기다리다 포기한 것)은 error.response가 없다. 이 구분이 없으면 클라이언트 타임아웃도 "확정
   *  실패"로 오인해 재시도를 열어주게 되고, 서버가 뒤늦게 실제로 성공시키면 이중결제로 이어진다
   *  (2026-09-07 발견·수정). */
  function isConfirmedServerResponse(error: unknown): boolean {
    return (error as { response?: unknown })?.response != null;
  }

  /** PAYMENT_STATUS_UNCONFIRMED(2026-09-11 BE 신설) — ExLink 자체가 응답 불가해 서버도 토스가 실제로
   *  승인했는지 모르는 상태. 서버가 응답을 보내긴 했지만(isConfirmedServerResponse=true) "확정 실패"가
   *  아니므로, 이 코드일 때는 클라이언트 타임아웃과 똑같이 취급해 멱등키를 지우거나 재시도를 열어주지
   *  않고 폴링(statusPollQuery)에 판단을 맡긴다 — 안 그러면 실제로는 성공한 결제 위에 새 결제가 또
   *  나가는 이중결제로 이어질 수 있다. */
  function isUnconfirmedPaymentStatus(error: unknown): boolean {
    const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
    return code === 'PAYMENT_STATUS_UNCONFIRMED';
  }

  const upgradeMutation = useMutation({
    mutationFn: () => {
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();
      // cycleParam은 미리보기(previewQuery)에 넘긴 것과 동일한 값 — 화면에 보여준 금액·주기와
      // 실제 청구가 반드시 같은 기준으로 계산되게 한다. selectedMethodId — 사용자가 고른 카드로
      // 이번 건만 청구되고 기본카드는 안 바뀐다(2026-09-18 정책 확정).
      return billingApi.upgradeTier(
        targetTier as TierCode,
        idempotencyKeyRef.current,
        agreed,
        cycleParam ?? undefined,
        selectedMethodId ?? undefined,
      );
    },
    onMutate: () => {
      setPaymentError(null);
      setAwaitingConfirmation(true);
    },
    onSuccess: () => {
      setSucceeded(true);
      setAwaitingConfirmation(false);
      idempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
    },
    onError: (error: unknown) => {
      // 폴링(아래)으로 이미 성공을 확인한 뒤 뒤늦게 도착한 에러라면 무시.
      if (succeeded) return;
      // 서버가 실제로 응답하지 않은 클라이언트 타임아웃이거나(isConfirmedServerResponse=false),
      // 서버는 응답했지만 확정 실패가 아닌 "아직 모름"(PAYMENT_STATUS_UNCONFIRMED)이면 재시도를
      // 열어주지 않고 폴링에 판단을 맡긴다(awaitingConfirmation을 계속 true로 유지).
      if (!isConfirmedServerResponse(error) || isUnconfirmedPaymentStatus(error)) return;
      // 여기부턴 서버가 실제로 실패를 응답한 확정 실패 — 안전하게 재시도할 수 있다. 재사용하면 서버의
      // 멱등키가 10분간 막고 있어(IdempotencyService TTL) 정상적인 재시도까지 함께 막히는 버그가
      // 있었다(2026-09-07 발견·수정).
      setAwaitingConfirmation(false);
      idempotencyKeyRef.current = null;
      // 화면처리기준 §6: PG 응답코드를 그대로 노출하지 않고 이해하기 쉬운 문구로 매핑 — BusinessException의
      // data 필드(토스 실제 실패 사유)를 그대로 보여준다(가공 없이도 이미 사람이 읽을 수 있는 한국어 문구).
      setPaymentError(extractErrorReason(error, '결제에 실패했습니다'));
    },
  });

  // 1회성 결제(결제수단 미등록) 승인 — handleOneTimePayment가 토스 결제창(requestPayment)을 열어
  // 이미 승인까지 끝낸 뒤 돌아온 paymentKey를 서버에 최종 확정만 요청한다. upgradeMutation과 같은
  // awaitingConfirmation/succeeded/slowResponse 상태를 공유해 성공 모달·10초 지연 안내·폴링
  // (statusPollQuery)이 그대로 적용된다 — 어느 쪽이 티어를 바꿨는지는 폴링이 상관하지 않는다.
  const oneTimePaymentMutation = useMutation({
    mutationFn: (payload: { paymentKey: string; orderId: string }) =>
      billingApi.confirmOneTimePayment(
        { targetTierCode: targetTier as TierCode, paymentKey: payload.paymentKey, orderId: payload.orderId },
        crypto.randomUUID(),
      ),
    onMutate: () => {
      setPaymentError(null);
      setAwaitingConfirmation(true);
    },
    onSuccess: () => {
      setSucceeded(true);
      setAwaitingConfirmation(false);
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
    },
    onError: (error: unknown) => {
      if (succeeded) return;
      if (!isConfirmedServerResponse(error) || isUnconfirmedPaymentStatus(error)) return;
      setAwaitingConfirmation(false);
      setPaymentError(extractErrorReason(error, '결제에 실패했습니다'));
    },
  });

  const trialMutation = useMutation({
    mutationFn: () => {
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();
      return billingApi.startTrial(
        { tierCode: targetTier as TierCode, billingCycle: trialBillingCycle, referrerMemberId },
        idempotencyKeyRef.current,
      );
    },
    onMutate: () => {
      setPaymentError(null);
      setAwaitingConfirmation(true);
    },
    onSuccess: () => {
      setSucceeded(true);
      setAwaitingConfirmation(false);
      idempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: (error: unknown) => {
      if (succeeded) return;
      if (!isConfirmedServerResponse(error)) return;
      setAwaitingConfirmation(false);
      idempotencyKeyRef.current = null;
      setPaymentError(extractErrorReason(error, '무료체험 시작에 실패했습니다'));
    },
  });

  // 화면처리기준 §8 — 10초 이상 무응답 시 안내(무한 로딩처럼 보이지 않게). mutation.isPending이
  // 아니라 awaitingConfirmation 기준 — axios 타임아웃(10초) 이후에도 "아직 확정 안 됨" 상태가
  // 계속돼야 이어지는 폴링(아래)이 끊기지 않는다.
  useEffect(() => {
    if (awaitingConfirmation) {
      slowTimerRef.current = setTimeout(() => setSlowResponse(true), SLOW_RESPONSE_MS);
    } else {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
      setSlowResponse(false);
    }
    return () => {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    };
  }, [awaitingConfirmation]);

  // 응답이 늦어질 때(slowResponse) 실제 구독 상태를 주기적으로 확인해 "재시도해도 되는지"를 시간이
  // 아니라 실제 결과로 판단한다(2026-09-07, 이중결제 위험 없이 재시도를 허용하기 위한 설계) — sv-billing
  // →ExLink→토스 재시도 체인이 정상 상황에서도 15~20초까지 걸릴 수 있어(Resilience4j 3회 재시도 포함),
  // axios 자체는 10초에 이미 포기하지만 서버는 그 뒤에도 계속 처리 중일 수 있다. 이 폴링이 "이미
  // 성공했음"을 먼저 확인하면 곧장 성공 처리한다. 반대로 실패는 폴링으로 판별 불가(티어가 안 바뀐 게
  // "아직 처리 중"인지 "실패"인지 구분 안 됨) — 그래서 실패 확정은 서버가 실제로 응답했을 때만(위
  // onError의 isConfirmedServerResponse) 내린다.
  const statusPollQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async ({ signal }) => (await billingApi.getSubscription(signal)).data.data,
    enabled: slowResponse && awaitingConfirmation && !succeeded,
    refetchInterval: 3000,
  });

  useEffect(() => {
    if (!awaitingConfirmation || succeeded || !statusPollQuery.data) return;
    const sub = statusPollQuery.data;
    const matched = isTrial
      ? sub.subStatus === 'TRIAL' && sub.tierCode === targetTier
      : sub.tierCode === targetTier;
    if (!matched) return;
    setSucceeded(true);
    setAwaitingConfirmation(false);
    idempotencyKeyRef.current = null;
    queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
  }, [statusPollQuery.data, awaitingConfirmation, succeeded, isTrial, targetTier, queryClient]);

  // 새로고침하면 awaitingConfirmation 등 진행 상태가 전부 초기화되지만, 서버는 그 사이 실제로
  // 결제를 끝냈을 수 있다(axios 10초 타임아웃 이후에도 최대 70초 안팎 계속 처리, 위 statusPollQuery
  // 주석 참고) — 이 화면은 그 사실을 전혀 모른 채 결제 폼을 다시 그려서, 사용자가 "안 됐나 보다"
  // 하고 재시도하면 이중결제로 이어질 수 있다(2026-09-11 리뷰 대응). statusPollQuery는
  // awaitingConfirmation이 true여야 도는데 새로고침 직후엔 이게 false라 안 돌아가므로, 마운트 시
  // awaitingConfirmation 여부와 무관하게 지금 구독 상태를 1회 확인해 이미 목표와 일치하면 곧장
  // 성공 처리한다. ⚠️ 이건 "이미 끝난 성공"만 잡아낸다 — 새로고침이 서버가 한창 처리 중인 바로 그
  // 순간에 일어나고 곧바로 재시도하는 경우까지는 못 막는다(서버에 "지금 처리 중인 요청이 있는지"를
  // 물어볼 API가 따로 있어야 완전히 막을 수 있음, 추후 과제).
  const initialStatusCheckQuery = useQuery({
    queryKey: ['billing', 'subscription', 'checkoutInitialCheck', targetTier, isTrial],
    queryFn: async ({ signal }) => (await billingApi.getSubscription(signal)).data.data,
    enabled: targetTier != null,
  });

  useEffect(() => {
    if (succeeded || !initialStatusCheckQuery.data) return;
    const sub = initialStatusCheckQuery.data;
    const matched = isTrial
      ? sub.subStatus === 'TRIAL' && sub.tierCode === targetTier
      : sub.tierCode === targetTier;
    if (!matched) return;
    setSucceeded(true);
    queryClient.invalidateQueries({ queryKey: ['billing', 'paymentMethods'] });
    queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
  }, [initialStatusCheckQuery.data, succeeded, isTrial, targetTier, queryClient]);

  // 결제 확인 중(awaitingConfirmation) 새로고침·이탈을 사용자가 직접 하지 않게 막는 안전장치 —
  // 위 initialStatusCheckQuery는 "이미 끝난 성공"만 잡아내고, 서버가 아직 처리 중인 도중에
  // 새로고침 후 곧바로 재시도하는 경우까지는 못 막는다(2026-09-11). 브라우저 기본 확인창은
  // 문구 커스터마이징이 안 되지만(최신 브라우저 공통 사양), 이탈 자체를 한 번 더 막아주는
  // 효과는 있어 결제 확인 중에만 등록한다.
  useEffect(() => {
    if (!awaitingConfirmation || redirectingToTossRef.current) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [awaitingConfirmation]);

  if (!targetTier) {
    return (
      <Page width="md">
        <PageHeader title="결제 확인" onBack={() => navigate('/agents/billing/manage')} />
        <ErrorState
          className="mt-08"
          title="잘못된 접근이에요"
          description="요금제 변경 화면에서 다시 시도해주세요."
        />
      </Page>
    );
  }

  // A1: 성공 = 얼럿(Modal)으로 알린 뒤 구독·결제 관리 화면으로 이동(사용자 요청, 2026-09-01) —
  // 확인 버튼·닫기(X)·바깥 클릭·Esc 어느 쪽으로 닫아도 결과는 동일하게 이동으로 처리한다.
  const goToSubscriptionManage = () => navigate('/agents/billing/manage');

  const preview = previewQuery.data;
  const trialTier = plansQuery.data?.tiers.find((t) => t.tierCode === targetTier);
  const trialMonthlyPrice = trialBillingCycle === 'annual' ? trialTier?.annualMonthlyDisplay : trialTier?.monthlyPrice;
  const canPay = isTrial
    ? agreed && !plansQuery.isLoading && !plansQuery.isError && !!trialTier && !referralCodeQuery.isLoading
    : paymentMode === 'ONE_TIME'
      ? agreed && !previewQuery.isLoading && !previewQuery.isError
      : agreed && selectedMethodId != null && !previewQuery.isLoading && !previewQuery.isError;

  return (
    <Page width="md">
      <PageHeader title="결제 확인" onBack={() => navigate(-1)} />

      {/* A1: 실패 = 경고배너(주문내용 위) + 정보수정 후 재시도 유지(화면 이탈 없음) */}
      {paymentError && (
        <Alert
          kind="error"
          className="mt-06"
          title={isTrial ? '체험 시작에 실패했어요' : '결제에 실패했어요'}
          onClose={() => setPaymentError(null)}
        >
          {paymentError}
        </Alert>
      )}

      {/* 주문 내용 (C-11-3) */}
      <Card className="mt-06">
        <h3 className="text-title-sm text-text">주문 내용</h3>
        {isTrial ? (
          plansQuery.isLoading ? (
            <Skeleton className="mt-04" height={100} />
          ) : plansQuery.isError || !trialTier ? (
            <ErrorState className="mt-04" title="주문 정보를 불러오지 못했습니다" />
          ) : (
            <>
              <div className="mt-04 flex items-center justify-between text-body-sm">
                <span className="text-text-muted">요금제</span>
                <span className="font-semibold text-text">{TIER_COPY[targetTier].displayName}</span>
              </div>
              <div className="mt-02 flex items-center justify-between text-body-sm">
                <span className="text-text-muted">체험 종료 후 정가(월)</span>
                <span className="text-text">₩{formatWon(trialMonthlyPrice)}</span>
              </div>
              <div className="mt-03 border-t border-border" />
              <div className="mt-03 flex items-center justify-between">
                <span className="text-body-sm text-text-muted">오늘 결제할 금액</span>
                <span className="text-title-md font-bold text-group">₩0</span>
              </div>
              <p className="mt-02 text-caption text-text-muted">
                {trialMonths}개월 무료체험이에요. 카드 등록 없이 시작할 수 있고, 체험이 끝나면
                자동으로 Free 요금제로 전환돼요.
              </p>
            </>
          )
        ) : previewQuery.isLoading ? (
          <Skeleton className="mt-04" height={100} />
        ) : previewQuery.isError || !preview ? (
          <ErrorState className="mt-04" title="주문 정보를 불러오지 못했습니다" />
        ) : (
          <>
            <div className="mt-04 flex items-center justify-between text-body-sm">
              <span className="text-text-muted">요금제</span>
              <span className="font-semibold text-text">{TIER_COPY[preview.targetTierCode].displayName}</span>
            </div>
            <div className="mt-02 flex items-center justify-between text-body-sm">
              {/* preview.targetMonthlyPrice는 이름과 달리, cycleParam으로 연간을 넘긴 첫 유료 전환이면
                  연간 정가가 그대로 들어있다(BE calcTierChange의 targetBasisPrice) — 라벨을 그에 맞춘다. */}
              <span className="text-text-muted">정가({cycleParam === 'annual' ? '연' : '월'})</span>
              <span className="text-text">₩{formatWon(preview.targetMonthlyPrice)}</span>
            </div>
            {/* 직원 추가요금이 있을 때만 내역을 나눠 보여준다(§24) — 없으면 기존처럼 합계만 표시 */}
            {preview.staffSurchargeAmt > 0 && (
              <div className="mt-02 flex items-center justify-between text-body-sm">
                <span className="text-text-muted">직원 추가요금(현재 인원 기준)</span>
                <span className="text-text">₩{formatWon(preview.staffSurchargeAmt)}</span>
              </div>
            )}
            <div className="mt-03 border-t border-border" />
            <div className="mt-03 flex items-center justify-between">
              <span className="text-body-sm text-text-muted">오늘 결제할 금액</span>
              <span className="text-title-md font-bold text-group">₩{formatWon(preview.proratedDiff)}</span>
            </div>
            <p className="mt-02 text-caption text-text-muted">
              {preview.remainingDays != null
                ? `이번 결제 주기 남은 기간(${preview.remainingDays}일)만큼의 차액이에요.`
                : `첫 ${cycleParam === 'annual' ? '연' : '달'} 요금 전액이에요.`}{' '}
              다음 결제일은 {formatDate(preview.currentPeriodEnd)}이에요.
            </p>
          </>
        )}
      </Card>

      {/* 결제수단 (C-11-4) — 무료체험은 카드 등록이 필수가 아니라 미등록이어도 막지 않는다 */}
      <Card className="mt-06">
        <h3 className="text-title-sm text-text">결제수단</h3>

        {/* 저장된 카드로 자동결제(기존) vs 카드정보를 매번 새로 입력하는 1회성 결제 — 무료체험은
            애초에 결제 자체가 없어 이 선택지가 의미 없다(카드 등록 없이 시작). */}
        {!isTrial && (
          <Segmented
            className="mt-04"
            options={[
              { label: '자동결제 (저장된 카드)', value: 'AUTO' },
              { label: '1회만 결제', value: 'ONE_TIME' },
            ]}
            value={paymentMode}
            onChange={(v) => setPaymentMode(v as PaymentMode)}
          />
        )}

        {!isTrial && paymentMode === 'ONE_TIME' && (
          <Alert kind="info" className="mt-04" title="1회만 결제하면">
            카드가 저장되지 않고, 이번 결제 한 번만 진행돼요. 다음 결제일에 자동으로 청구되지
            않으니, 다음 달에도 이용하려면 그때 다시 결제해주셔야 해요.
          </Alert>
        )}

        {(isTrial || paymentMode === 'AUTO') && (
          <>
            {cardRegistrationError && (
              <Alert
                kind="error"
                className="mt-04"
                title="카드 등록에 실패했어요"
                onClose={() => setCardRegistrationError(null)}
              >
                {cardRegistrationError}
              </Alert>
            )}

            {registerCardMutation.isPending ? (
              <Alert kind="info" className="mt-04">
                카드 등록을 처리하고 있어요...
              </Alert>
            ) : paymentMethodsQuery.isLoading ? (
              <Skeleton className="mt-04" height={40} />
            ) : selectedMethod ? (
              <div className="mt-04 text-body-sm">
                <span className="text-text">{formatPaymentMethodLabel(selectedMethod)}</span>
              </div>
            ) : isTrial ? (
              <p className="mt-04 text-body-sm text-text-muted">무료체험은 카드 등록 없이 시작할 수 있어요.</p>
            ) : (
              <EmptyState
                className="mt-04"
                title="등록된 결제수단이 없어요"
                description="결제를 진행하려면 카드를 먼저 등록해주세요."
              />
            )}

            {/* 등록된 카드가 여러 장이면 가로로 넘겨보며 이번 결제에 쓸 카드를 바로 고를 수 있다 */}
            {paymentMethodsQuery.data && paymentMethodsQuery.data.length > 0 && (
              <>
                <div className="mt-04 border-t border-border" />
                <p className="mt-04 text-body-sm text-text-muted">등록된 결제수단</p>
                {/* ui-core .card-list.row는 overflow-x:auto만 걸려있어 OS 기본(굵고 각진) 스크롤바가
                    그대로 노출된다(공통 스타일, 다른 에이전트도 동일 — 추후작업_TODO.md 참고). 완전히
                    숨기면 마우스 사용자가 넘기는 방법 자체를 모르게 되니(사용자 피드백, 2026-09-01),
                    숨기는 대신 얇고 둥근 형태로만 다시 그린다 — 스크롤 가능하다는 신호(마우스 드래그로
                    직접 끌 수도 있음)는 유지하면서 기본 스크롤바보다 튀지 않게. 공통 패키지는 안 건드리고
                    이 페이지에서만 국소 적용. */}
                <style>{`
                  .checkout-card-scroll { scrollbar-width: thin; scrollbar-color: var(--shell-border) transparent; }
                  .checkout-card-scroll::-webkit-scrollbar { height: 6px; }
                  .checkout-card-scroll::-webkit-scrollbar-track { background: transparent; }
                  .checkout-card-scroll::-webkit-scrollbar-thumb { background: var(--shell-border); border-radius: 9999px; }
                `}</style>
                <CardList layout="row" className="checkout-card-scroll mt-03 pb-02">
                  {paymentMethodsQuery.data.map((method) => (
                    <PaymentMethodTile
                      key={method.paymentMethodId}
                      method={method}
                      onSetDefault={() => setDefaultMutation.mutate(method.paymentMethodId)}
                      onDelete={() => setPendingDeleteId(method.paymentMethodId)}
                      onSaveAlias={(cardAlias) => updateAliasMutation.mutate({ paymentMethodId: method.paymentMethodId, cardAlias })}
                      settingDefault={
                        setDefaultMutation.isPending &&
                        setDefaultMutation.variables === method.paymentMethodId
                      }
                      savingAlias={
                        updateAliasMutation.isPending &&
                        updateAliasMutation.variables?.paymentMethodId === method.paymentMethodId
                      }
                      // 이번 결제에 쓸 카드 = 로컬 선택 상태(기본카드와는 별개, 2026-09-18 정책
                      // 확정) — 무료체험은 지금 당장 청구가 없어 카드를 골라도 적용될 곳이 없으므로
                      // 클릭 자체를 막아둔다(정보 표시용 하이라이트만 유지).
                      selected={method.paymentMethodId === selectedMethodId}
                      onSelect={isTrial ? undefined : () => setSelectedPaymentMethodId(method.paymentMethodId)}
                    />
                  ))}
                  {!isTrial && (
                    <AddPaymentMethodTile onClick={handleRegisterCard} loading={registeringCard} />
                  )}
                </CardList>
              </>
            )}

            {/* 카드가 하나도 없을 땐 옆에 붙일 목록 자체가 없어 위 목록 대신 버튼으로 대체 노출 */}
            {!isTrial && (!paymentMethodsQuery.data || paymentMethodsQuery.data.length === 0) && (
              <Button
                variant="secondary"
                className="mt-05 w-full sm:w-auto"
                onClick={handleRegisterCard}
                loading={registeringCard}
                disabled={registeringCard}
              >
                카드 등록하기
              </Button>
            )}
          </>
        )}
      </Card>

      {/* 결제 버튼 처리 (C-11-5) */}
      <Card className="mt-06">
        <Checkbox
          label={
            isTrial ? (
              '주문 내용을 확인했고 무료체험 시작에 동의해요'
            ) : (
              <>
                {paymentMode === 'ONE_TIME'
                  ? '이번 1회 결제만 진행하며, 자동으로 갱신되지 않는다는 점을 확인했어요 (필수)'
                  : '정기 결제 및 자동 갱신에 동의합니다 (필수)'}{' '}
                ·{' '}
                <Button
                  variant="ghost"
                  size="sm"
                  className="!h-auto !p-0 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // 토스페이먼츠 전자금융거래 기본약관(이용자용) — 전자지급결제대행서비스 관련
                    // 법정 고지 문서라 가맹점 심사 상태와 무관하게 상시 공개된 URL이다.
                    window.open('https://pages.tosspayments.com/terms/user/', '_blank', 'noopener,noreferrer');
                  }}
                >
                  전자상거래 결제대행 이용약관 보기
                </Button>
              </>
            )
          }
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <p className="mt-02 text-body-sm text-danger">7일 이내 미사용 시 전액환불 가능해요.</p>

        {/* 결제 확인 중엔 새로고침·화면 이탈 시 이중결제로 이어질 수 있어(위 beforeunload 핸들러와
            같은 목적) 처리 중임을 계속 눈에 띄게 안내한다 — slowResponse(10초 경과)를 기다리지 않고
            awaitingConfirmation이 되는 즉시 보여준다. */}
        {awaitingConfirmation && (
          <Alert kind="warning" className="mt-04">
            결제가 완료되기 전까지 이 화면을 벗어나거나 새로고침하지 말고 기다려주세요.
          </Alert>
        )}

        {slowResponse && (
          <Alert kind="info" className="mt-04">
            확인하는 데 시간이 걸리고 있어요, 잠시 후 다시 확인해 주세요.
          </Alert>
        )}

        <Button
          variant="primary"
          className="mt-04 w-full"
          // (2026-09-18) 결제에 쓸 카드가 이제 selectedMethodId(로컬 상태)로 즉시 결정되고 서버
          // upgradeTier에 명시적으로 실어보내므로, registerCardMutation.onSuccess가 더 이상
          // setDefaultMutation을 fire-and-forget으로 보내지 않는다 — 예전엔 그 반영 전에 결제
          // 버튼을 누르면 엉뚱한(이전) 기본카드로 청구될 위험이 있어 setDefaultMutation.isPending도
          // 함께 막았지만(2026-09-11), 이제 "기본으로 설정"은 결제 대상 카드와 무관한 별개 동작이라
          // 그 요청 진행 중이어도 결제 버튼을 막을 이유가 없다.
          disabled={!canPay || awaitingConfirmation}
          loading={awaitingConfirmation}
          onClick={() => {
            if (isTrial) trialMutation.mutate();
            else if (paymentMode === 'ONE_TIME') handleOneTimePayment();
            else upgradeMutation.mutate();
          }}
        >
          {awaitingConfirmation
            ? isTrial
              ? '체험을 시작하고 있어요...'
              : '결제를 확인하고 있어요...'
            : isTrial
              ? `${trialMonths}개월 무료체험 시작하기`
              : paymentMode === 'ONE_TIME'
                ? `₩${formatWon(preview?.proratedDiff)} 1회 결제하기`
                : `₩${formatWon(preview?.proratedDiff)} 결제하기`}
        </Button>
      </Card>

      <Modal
        open={succeeded}
        onClose={goToSubscriptionManage}
        title={isTrial ? '무료체험이 시작됐어요' : '결제가 완료됐어요'}
      >
        <div className="flex flex-col items-center gap-md text-center">
          <IconCheckCircle width={40} height={40} className="text-success" />
          <p className="text-body-sm text-text">
            {isTrial ? `${trialMonths}개월 무료체험이 시작됐어요.` : '결제가 성공적으로 완료되었어요.'}
          </p>
          <p className="text-body-sm text-text-muted">{TIER_COPY[targetTier].displayName} 요금제로 전환됐어요.</p>
          <Button variant="primary" className="w-full" onClick={goToSubscriptionManage}>
            구독·결제 관리로 이동
          </Button>
        </div>
      </Modal>

      {/* PaymentMethodPage.tsx와 동일한 삭제 확인 모달(2026-09-16 — PaymentMethodTile 공용화로 함께 추가) */}
      <Modal open={pendingDeleteId != null} onClose={() => setPendingDeleteId(null)} title="결제수단을 삭제할까요?">
        <div className="flex flex-col gap-sm">
          <p className="text-body-sm text-text-muted">삭제한 결제수단은 되돌릴 수 없어요.</p>
          <div className="mt-03 flex w-full gap-sm">
            <Button variant="secondary" className="flex-1" onClick={() => setPendingDeleteId(null)}>
              취소
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              loading={deleteMutation.isPending}
              disabled={deleteMutation.isPending}
              onClick={() => pendingDeleteId != null && deleteMutation.mutate(pendingDeleteId)}
            >
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </Page>
  );
}

interface AddPaymentMethodTileProps {
  onClick: () => void;
  loading: boolean;
}

/** 등록된 결제수단 목록 맨 끝에 붙는 타일 — "등록된 결제수단 외의 것으로 결제하고 싶다"는 요청에 대응.
 * 클릭하면 새 카드를 등록하고(handleRegisterCard), 등록 즉시 기본 결제수단으로 전환해 이번 결제부터
 * 그 카드로 청구되게 한다(registerCardMutation.onSuccess 참고). */
function AddPaymentMethodTile({ onClick, loading }: AddPaymentMethodTileProps) {
  const clickable = !loading;
  return (
    <Card
      className="w-64 shrink-0 cursor-pointer border-2 border-dashed border-border"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex h-full min-h-16 flex-col items-center justify-center gap-xs text-center">
        <span className="text-body-sm font-semibold text-text-muted">
          {loading ? '등록 중...' : '+ 카드 등록하기'}
        </span>
      </div>
    </Card>
  );
}
